import React, { useEffect, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { fetchOrgs } from './api';
import type { AdminOrg, OrgsMeta } from './types';
import { compactSelectClass, secondaryBtnClass } from '../styles';

interface OrgPickerListProps {
  meta: OrgsMeta;
  mode: 'single' | 'multi';
  selectedIds: number[];
  onChange: (ids: number[], orgs: AdminOrg[]) => void;
  refreshToken?: number;
}

const OrgPickerList: React.FC<OrgPickerListProps> = ({
  meta,
  mode,
  selectedIds,
  onChange,
  refreshToken = 0,
}) => {
  const [q, setSearch] = useState('');
  const [tier, setTier] = useState<number | ''>('');
  const [isActive, setIsActive] = useState<'' | 'true' | 'false'>('true');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<AdminOrg[]>([]);
  const [count, setCount] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async (nextPage = page) => {
    setLoading(true);
    setError(null);
    try {
      const payload = await fetchOrgs({
        q: q.trim(),
        tier,
        is_active: isActive,
        page: nextPage,
      });
      setData(payload.results);
      setCount(payload.count);
      setPage(payload.page);
      setPageSize(payload.page_size);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (refreshToken) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const selectedSet = new Set(selectedIds);
  const visibleSelected = data.filter((org) => selectedSet.has(org.id));
  const allVisibleSelected = data.length > 0 && visibleSelected.length === data.length;

  const toggle = (org: AdminOrg) => {
    if (mode === 'single') {
      onChange([org.id], [org]);
      return;
    }
    const next = selectedSet.has(org.id)
      ? selectedIds.filter((id) => id !== org.id)
      : [...selectedIds, org.id];
    const retained = data.filter((item) => next.includes(item.id));
    onChange(next, retained);
  };

  const togglePage = () => {
    if (mode !== 'multi') return;
    if (allVisibleSelected) {
      onChange(
        selectedIds.filter((id) => !data.some((org) => org.id === id)),
        []
      );
    } else {
      onChange(Array.from(new Set([...selectedIds, ...data.map((org) => org.id)])), data);
    }
  };

  const ordered = [...data].sort((a, b) => {
    const aIsCurrent = a.id === meta.current_org_id;
    const bIsCurrent = b.id === meta.current_org_id;
    if (aIsCurrent && !bIsCurrent) return -1;
    if (!aIsCurrent && bIsCurrent) return 1;
    return a.name.localeCompare(b.name);
  });

  const totalPages = Math.max(1, Math.ceil(count / pageSize));

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative w-64 max-w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A9A]" />
          <input
            value={q}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void load(1);
            }}
            placeholder="Search organizations"
            className="h-10 w-full rounded-md border border-[#D1D1D1] pl-9 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#3da3d4]"
          />
        </div>
        <select
          value={tier}
          onChange={(e) => setTier(e.target.value ? Number(e.target.value) : '')}
          className={compactSelectClass}
          aria-label="Filter by tier"
        >
          <option value="">All tiers</option>
          {meta.tiers.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
        <select
          value={isActive}
          onChange={(e) => setIsActive(e.target.value as '' | 'true' | 'false')}
          className={compactSelectClass}
          aria-label="Filter by status"
        >
          <option value="">Active and inactive</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
        <button type="button" onClick={() => void load(1)} className={secondaryBtnClass}>
          Search
        </button>
      </div>
      {error && (
        <div className="mb-3 rounded-md bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>
      )}
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[#8A8A8A] py-6">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading organizations…
        </div>
      ) : (
        <div className="overflow-x-auto border border-[#E8E8E8] rounded-lg">
          <table className="w-full min-w-[800px] text-sm">
            <thead className="bg-[#FAFAFA] text-left text-[12px] text-[#626262]">
              <tr>
                <th className="px-3 py-2.5 w-10">
                  {mode === 'multi' && (
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={togglePage}
                      aria-label="Select all on this page"
                    />
                  )}
                </th>
                <th className="px-3 py-2.5 font-semibold">Name</th>
                <th className="px-3 py-2.5 font-semibold">Tier</th>
                <th className="px-3 py-2.5 font-semibold">Currency</th>
                <th className="px-3 py-2.5 font-semibold">Account manager</th>
                <th className="px-3 py-2.5 font-semibold">Members</th>
                <th className="px-3 py-2.5 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {ordered.map((org) => {
                const selected = selectedSet.has(org.id);
                return (
                  <tr
                    key={org.id}
                    className={`border-t border-[#E8E8E8] cursor-pointer ${
                      selected ? 'bg-[#F3F8FC]' : 'hover:bg-[#FAFAFA]'
                    }`}
                    onClick={() => toggle(org)}
                  >
                    <td className="px-3 py-2.5">
                      <input
                        type={mode === 'multi' ? 'checkbox' : 'radio'}
                        checked={selected}
                        onChange={() => toggle(org)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td className="px-3 py-2.5 text-[#0C0B0B]">{org.name}</td>
                    <td className="px-3 py-2.5">{org.tier_name || '—'}</td>
                    <td className="px-3 py-2.5">{org.currency_code || '—'}</td>
                    <td className="px-3 py-2.5">{org.account_manager_name || '—'}</td>
                    <td className="px-3 py-2.5">{org.client_member_count}</td>
                    <td className="px-3 py-2.5">
                      {org.is_active ? (
                        <span className="text-emerald-700">Active</span>
                      ) : (
                        <span className="text-[#8A8A8A]">Inactive</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {data.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-[#8A8A8A]">
                    No organizations match these filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-3 flex items-center justify-between text-[12px] text-[#626262]">
        <span>
          {count} organization{count === 1 ? '' : 's'}
          {mode === 'multi' && selectedIds.length > 0 ? ` · ${selectedIds.length} selected` : ''}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="text-[#3da3d4] disabled:opacity-30"
            disabled={page <= 1 || loading}
            onClick={() => void load(page - 1)}
          >
            Previous
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            className="text-[#3da3d4] disabled:opacity-30"
            disabled={page >= totalPages || loading}
            onClick={() => void load(page + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrgPickerList;
